import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UpdateStudyInformationRequest } from '@app/generated-api';

import {
    GetAllAccessibleStudiesResponse,
    GetAllCrcAccessibleStudiesResponse,
    GetAllStudiesResponse,
    GetStudiesResponse,
    GetStudyOverviewResponse,
    GetStudyPatientsResponse,
    GetStudyUtilizationDataResponse,
} from '@app/generated-api';
import { createLazyLoadedState } from '@app/v2/utility';
import { getSiteBaseUrl } from '@utility/utility';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class StudyServiceV2 {
    private httpClient = inject(HttpClient);

    readonly activeStudies = createLazyLoadedState(() => ({
        fetch$: () =>
            this.httpClient
                .get<GetStudiesResponse>(`${getSiteBaseUrl()}/api/v2/studies/active`)
                .pipe(map((res) => res.studies)),
    }));

    getAllStudySummaries: () => Observable<GetAllAccessibleStudiesResponse[]> = () => {
        return this.httpClient.get<GetAllAccessibleStudiesResponse[]>(`${getSiteBaseUrl()}/api/v2/studies/list`);
    };

    getAllCrcStudySummaries: () => Observable<GetAllCrcAccessibleStudiesResponse[]> = () => {
        return this.httpClient.get<GetAllCrcAccessibleStudiesResponse[]>(`${getSiteBaseUrl()}/api/v2/studies/crc`);
    };

    readonly studyOverview = createLazyLoadedState(() => ({
        fetch$: (studyId: number) =>
            this.httpClient.get<GetStudyOverviewResponse>(`${getSiteBaseUrl()}/api/v2/studies/overview/${studyId}`),
    }));

    getStudyPatients: (studyId: number) => Observable<GetStudyPatientsResponse[]> = (studyId: number) => {
        return this.httpClient.get<GetStudyPatientsResponse[]>(
            `${getSiteBaseUrl()}/api/v2/studies/${studyId}/patients`
        );
    };

    closeStudy: (studyId: number) => Observable<number> = (studyId: number) => {
        return this.httpClient
            .patch(`${getSiteBaseUrl()}/api/v2/studies/${studyId}/close`, {}, { observe: 'response' })
            .pipe(map((response) => response.status));
    };

    getStudyUtilizationData: (studyId: number) => Observable<GetStudyUtilizationDataResponse> = (studyId: number) => {
        return this.httpClient.get<GetStudyUtilizationDataResponse>(
            `${getSiteBaseUrl()}/api/v2/studies/${studyId}/utilization`
        );
    };

    getAllStudies: () => Observable<GetAllStudiesResponse[]> = () => {
        return this.httpClient.get<GetAllStudiesResponse[]>(`${getSiteBaseUrl()}/api/v2/studies/all`);
    };

    updateSiteInfo: (request: UpdateStudyInformationRequest) => Observable<void> = (
        request: UpdateStudyInformationRequest
    ) => {
        return this.httpClient.post<void>(`${getSiteBaseUrl()}/api/v2/studies/update/info`, request);
    };
}
